import React from 'react';
import { Badge } from '@mui/material';
import {
  Assignment, AssignmentInd, AssignmentTurnedIn, AccountTree, AdminPanelSettings, Assessment,
  Business, Build, CloudSync, Code, ContactMail, Dns, Error, Groups, Handshake,
  Help, History, LocalShipping, MenuBook, MarkEmailRead, AlternateEmail, Notifications, People,
  Person, Psychology, Receipt, Schedule, Science, Security, Settings, Speed, Storage, Support, TableChart, Task,
  TravelExplore, TrendingUp, VerifiedUser, VpnKey, Work,
  Dashboard as DashboardIcon, ShoppingCart as CartIcon, Category as CategoryIcon,
} from '@mui/icons-material';

/**
 * Sidebar navigation config for every portal mode, extracted from Layout.tsx.
 * Each menu is a `build*Menu(counts)` factory rather than a static array so any
 * item can carry a live notification badge — pass the runtime counts in and the
 * relevant icons render a <Badge>. Access flags (component / requiredRole /
 * skipComponentAccess / allowCrm* / requiredCrmLevel / requiresAny) are consumed
 * unchanged by Layout's filterMenuByAccess.
 */
export interface NavBadgeCounts {
  deptPendingCount?: number;
  pendingOrderRequests?: number;
}

/** Fields shared by both nav-item kinds (label/icon + the access-gate flags). */
interface NavItemBase {
  text?: string;
  label?: string;
  icon?: React.ReactNode;
  component?: string;
  requiresAny?: string[];
  skipComponentAccess?: boolean;
  requiredRole?: string;
  requiredCrmLevel?: string;
  allowCrmRestricted?: boolean;
  allowCrmManage?: boolean;
}

/** A leaf link — has a `path`, never a `group`/`items`. */
export interface NavLeaf extends NavItemBase {
  path: string;
  group?: never;
  items?: never;
}

/** An expandable group — has a `group` key + child `items`, never a `path`. */
export interface NavGroup extends NavItemBase {
  group: string;
  items: NavMenuItem[];
  path?: never;
}

/**
 * Discriminated union: a nav item is EITHER a leaf link (has `path`) OR a group
 * (has `group` + `items`). Lets `NavItem` narrow on `item.path` / `item.group`
 * instead of runtime-asserting with `!`.
 */
export type NavMenuItem = NavLeaf | NavGroup;

export const buildCrmMenu = ({ deptPendingCount, pendingOrderRequests }: NavBadgeCounts): NavMenuItem[] => ([
    // Core items (ungrouped)
    { text: 'My Assignments', icon: <Badge badgeContent={deptPendingCount || 0} color="error"><AssignmentInd /></Badge>, path: '/my-assignments', skipComponentAccess: true },
    { text: 'My Employees Assignments', icon: <Groups />, path: '/my-employees-assignments', skipComponentAccess: true, requiredRole: 'system_admin' },

    // Projects Group
    {
      group: 'projects',
      label: 'Projects',
      icon: <Work />,
      items: [
        { text: 'Projects', icon: <Assignment />, path: '/projects' },
        { text: 'Tasks', icon: <Task />, path: '/tasks' },
        { text: 'Project Templates', icon: <AssignmentInd />, path: '/project-templates' },
        { text: 'Task Templates', icon: <AssignmentInd />, path: '/task-templates' },
      ]
    },

    // Business Group
    {
      group: 'business',
      label: 'Business',
      icon: <Handshake />,
      items: [
        { text: 'Clients', icon: <ContactMail />, path: '/clients' },
        { text: 'Tickets', icon: <Support />, path: '/tickets' },
      ]
    },

    // Inventory Group
    {
      group: 'inventory',
      label: 'Inventory',
      icon: <Badge badgeContent={pendingOrderRequests} color="error"><Assignment /></Badge>,
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/inventory', component: 'inventory-dashboard' },
        { text: 'Stock Levels', icon: <Assessment />, path: '/inventory/stock', component: 'inventory-stock' },
        {
          group: 'inventory-admin',
          label: 'Inventory Admin',
          icon: <AdminPanelSettings />,
          items: [
            { text: 'Items', icon: <Science />, path: '/inventory/items', component: 'inventory-items' },
            { text: 'Departments', icon: <CategoryIcon />, path: '/inventory/departments', component: 'inventory-items' },
            { text: 'Locations', icon: <Business />, path: '/inventory/locations', component: 'inventory-locations' },
          ]
        },
        { text: 'Shipments', icon: <LocalShipping />, path: '/inventory/shipments', component: 'inventory-shipments' },
        { text: 'Receiving', icon: <Assignment />, path: '/inventory/receiving', component: 'inventory-receiving' },
        { text: 'Order Requests', icon: <Badge badgeContent={pendingOrderRequests} color="error"><CartIcon /></Badge>, path: '/inventory/order-requests', component: 'inventory-order-requests', allowCrmRestricted: true },
        { text: 'Purchase Orders', icon: <Receipt />, path: '/inventory/purchase-orders', component: 'inventory-purchase-orders' },
        { text: 'Transaction Log', icon: <History />, path: '/inventory/transactions', component: 'inventory-transactions' },
      ]
    },


    // Reports Group — each report is a separate nav entry with its own component access key
    {
      group: 'reports',
      label: 'Reports',
      icon: <Assessment />,
      items: [

        { text: 'Access Audit', icon: <Security />, path: '/reports/access-audit', component: 'report-access-audit' },
        { text: 'Project Status', icon: <Assignment />, path: '/reports/projects', component: 'report-projects' },
        { text: 'Employee / Team', icon: <Person />, path: '/reports/employee', component: 'report-employee' },
        { text: 'Ticket Reports', icon: <Support />, path: '/reports/tickets', component: 'report-tickets' },
        { text: 'Task Completion', icon: <AssignmentTurnedIn />, path: '/reports/task-completion', component: 'report-task-completion' },
        { text: 'Company Hierarchy', icon: <Business />, path: '/reports/company-hierarchy', component: 'report-company-hierarchy' },
        { text: 'LIS Test Types by Client', icon: <Science />, path: '/reports/lis-test-types-by-client', requiresAny: ['report-lis-test-types-by-client', 'crm'], allowCrmRestricted: true },
        // Dual-audience: CRM 'restricted' salespeople (own report) + CRM managers/admins (all + filter).
        { text: 'Sales Performance', icon: <TrendingUp />, path: '/reports/sales-performance', component: 'report-sales-performance', allowCrmRestricted: true, allowCrmManage: true },
        // CRM-admins-only (crm/manage gate — the per-report component was retired);
        // lives here with the other reports, not under Administration.
        { text: 'Patient Portal Access', icon: <Assessment />, path: '/reports/patient-portal-access', skipComponentAccess: true, requiredCrmLevel: 'manage' },
      ]
    },

    // Insights Group
    {
      group: 'insights',
      label: 'Insights',
      icon: <TrendingUp />,
      items: [
        { text: 'Daily Activity', icon: <AssignmentTurnedIn />, path: '/reports/daily-activity', component: 'report-daily-activity' },
        { text: 'User Activity', icon: <Assessment />, path: '/reports/user-activity', component: 'user-activity-report' },
        { text: 'Analytics', icon: <Assessment />, path: '/analytics' },
      ]
    },

    // Organization Group
    {
      group: 'organization',
      label: 'Organization',
      icon: <Business />,
      items: [
        { text: 'Departments', icon: <Groups />, path: '/departments', skipComponentAccess: true },
        { text: 'Company Structure', icon: <AccountTree />, path: '/organization/structure', skipComponentAccess: true },
      ]
    },

    // Admin Group
    {
      group: 'admin',
      label: 'Administration',
      icon: <AdminPanelSettings />,
      items: [
        { text: 'Companies', icon: <Business />, path: '/companies' },
        { text: 'Users', icon: <People />, path: '/users' },
        { text: 'External Users', icon: <ContactMail />, path: '/external-users' },
        { text: 'Audit Logs', icon: <History />, path: '/audit-logs' },
        { text: 'Settings', icon: <Settings />, path: '/settings', requiredRole: 'system_admin' },
        { text: 'Notification Settings', icon: <Notifications />, path: '/settings/notifications', requiredRole: 'system_admin' },
        { text: 'User Access Governance', icon: <VerifiedUser />, path: '/access-governance', component: 'access-governance' },
        { text: 'Category Management', icon: <TableChart />, path: '/admin/categories', requiredRole: 'system_admin' },
        { text: 'AI Interaction Log', icon: <Psychology />, path: '/admin/ai-interactions', requiredRole: 'system_admin' },
        { text: 'LIS Configurations', icon: <VpnKey />, path: '/lis/configurations', requiredRole: 'system_admin' },
        { text: 'LIS Query Configuration', icon: <Code />, path: '/lis/query-configurations', requiredRole: 'system_admin' },
      ]
    },

    // Tools Group
    {
      group: 'tools',
      label: 'Tools',
      icon: <Build />,
      items: [
        { text: 'Failed HPV/ISH Files', icon: <Error />, path: '/tools-hpv-ish-report-errors' },
      ]
    },

    // Resources Group
    {
      group: 'resources',
      label: 'Resources',
      icon: <MenuBook />,
      items: [
        { text: 'Knowledge Base', icon: <Help />, path: '/knowledge-base' },
      ]
    },
  ]
);

export const buildLisMenu = (_counts: NavBadgeCounts): NavMenuItem[] => ([
    // Core Items (ungrouped)
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/lis' },

    // Orders & Testing Group
    {
      group: 'testing',
      label: 'Orders & Testing',
      icon: <Science />,
      items: [
        { text: 'Locator', icon: <TravelExplore />, path: '/lis/locator' },
        { text: 'Received Orders', icon: <Assignment />, path: '/lis/orders' },
        { text: 'Test Types by Client', icon: <Assessment />, path: '/reports/lis-test-types-by-client', requiresAny: ['report-lis-test-types-by-client', 'crm'], allowCrmRestricted: true },
      ]
    },

    // Laboratory Management Group
    {
      group: 'lab_management',
      label: 'Lab Management',
      icon: <Science />,
      items: [
        { text: 'Medical Tests', icon: <Science />, path: '/medical-tests' },
      ]
    },

    {
      group: 'lis_admin',
      label: 'Administration',
      icon: <AdminPanelSettings />,
      items: [
        { text: 'Companies', icon: <Business />, path: '/companies' },
        { text: 'Users', icon: <People />, path: '/users' },
        { text: 'External Users', icon: <ContactMail />, path: '/external-users' },
        { text: 'Departments', icon: <Groups />, path: '/departments', skipComponentAccess: true },
        { text: 'Reports', icon: <Assessment />, path: '/admin/reports', component: 'users' },
        { text: 'Category Management', icon: <TableChart />, path: '/admin/categories', requiredRole: 'system_admin' },
        { text: 'AI Interaction Log', icon: <Psychology />, path: '/admin/ai-interactions', requiredRole: 'system_admin' },
        { text: 'LIS Configurations', icon: <VpnKey />, path: '/lis/configurations', requiredRole: 'system_admin' },
        { text: 'LIS Query Configuration', icon: <Code />, path: '/lis/query-configurations', requiredRole: 'system_admin' },
      ]
    },

    // Resources
    {
      group: 'lis_resources',
      label: 'Resources',
      icon: <MenuBook />,
      items: [
        { text: 'Knowledge Base', icon: <Help />, path: '/knowledge-base' },
        { text: 'Documents', icon: <Help />, path: '/documents' },
      ]
    },
  ]
);

export const buildBillingMenu = (_counts: NavBadgeCounts): NavMenuItem[] => ([
    // Orders Group
    {
      group: 'billing_orders',
      label: 'Orders',
      icon: <Receipt />,
      items: [
        // The `/billing` orders-list page and its status shortcuts (All Orders,
        // Pending Approval, Approved, Sent to Billing) are hidden from nav — that
        // workflow is unused; Daily Monitoring (the billers' spreadsheet-replacement
        // worklist, docs/features/daily-monitoring-report.md) replaces it. The
        // `/billing` route stays mounted so order-detail back-buttons and deep links
        // keep working.
        { text: 'Daily Monitoring', icon: <Schedule />, path: '/billing/monitoring', component: 'billing-daily-monitoring' },
        { text: 'Claims', icon: <Assignment />, path: '/billing/claims', component: 'billing-daily-monitoring' },
      ]
    },

    // Patients (LIS-backed patient directory search)
    {
      group: 'billing_patients',
      label: 'Patients',
      icon: <People />,
      items: [
        { text: 'Patient Search', icon: <People />, path: '/billing/patients' },
        { text: 'Locator', icon: <TravelExplore />, path: '/billing/locator' },
      ]
    },

    // Verification of Benefits (lives under billing per the subsystem layout)
    {
      group: 'billing_vob',
      label: 'Verification of Benefits',
      icon: <VerifiedUser />,
      items: [
        { text: 'VOB Queue', icon: <VerifiedUser />, path: '/billing/vob', skipComponentAccess: true, requiredRole: 'system_admin' },
      ]
    },

    // Laboratory Integration Group
    {
      group: 'lab_integration',
      label: 'Laboratory',
      icon: <Science />,
      items: [
        { text: 'LIS Portal', icon: <Science />, path: '/lis' },
      ]
    },

    // Admin
    {
      group: 'billing_admin',
      label: 'Administration',
      icon: <AdminPanelSettings />,
      items: [
        { text: 'Companies', icon: <Business />, path: '/companies' },
        { text: 'Users', icon: <People />, path: '/users' },
        { text: 'External Users', icon: <ContactMail />, path: '/external-users' },
        { text: 'Reports', icon: <Assessment />, path: '/admin/reports', component: 'users' },
        { text: 'Category Management', icon: <TableChart />, path: '/admin/categories', requiredRole: 'system_admin' },
        { text: 'AI Interaction Log', icon: <Psychology />, path: '/admin/ai-interactions', requiredRole: 'system_admin' },
      ]
    },

    // Resources
    {
      group: 'billing_resources',
      label: 'Resources',
      icon: <MenuBook />,
      items: [
        { text: 'Knowledge Base', icon: <Help />, path: '/knowledge-base' },
        { text: 'Documents', icon: <Help />, path: '/documents' },
      ]
    },
  ]
);

export const buildVobMenu = (_counts: NavBadgeCounts): NavMenuItem[] => ([
    {
      group: 'vob_main',
      label: 'Verification of Benefits',
      icon: <VerifiedUser />,
      items: [
        { text: 'VOB Queue', icon: <VerifiedUser />, path: '/billing/vob', skipComponentAccess: true, requiredRole: 'system_admin' },
      ]
    },
  ]
);

export const buildIntegrationsMenu = (_counts: NavBadgeCounts): NavMenuItem[] => ([
    // Core item (ungrouped) — the portal landing / status hub
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/integrations', skipComponentAccess: true },

    // Services Group — the connectors users configure
    {
      group: 'integration_services',
      label: 'Services',
      icon: <CloudSync />,
      items: [
        { text: 'Google Workspace', icon: <CloudSync />, path: '/google-workspace' },
        { text: 'Meraki VPN', icon: <VpnKey />, path: '/meraki-vpn', requiredRole: 'system_admin', skipComponentAccess: true },
        { text: 'Email Routing', icon: <AlternateEmail />, path: '/email-integration/routing', requiredRole: 'system_admin', skipComponentAccess: true },
        { text: 'CollaborateMD API Console', icon: <Code />, path: '/integrations/collaboratemd-console', requiredRole: 'system_admin', skipComponentAccess: true },
      ]
    },

    // Monitoring Group — the integration reports / event feeds
    {
      group: 'integration_monitoring',
      label: 'Monitoring',
      icon: <Assessment />,
      items: [
        { text: 'System Integration Activity', icon: <Assessment />, path: '/tools/s3-upload-activity', component: 'integrations-file-monitor' },
        { text: 'PGx Pipeline', icon: <Assessment />, path: '/integrations/pgx-pipeline', component: 'integrations-pgx-pipeline' },
        { text: 'Completed Orders', icon: <CloudSync />, path: '/integrations/completed-orders', component: 'integrations-completed-orders' },
        { text: 'Inbound-Email Events', icon: <MarkEmailRead />, path: '/tools/email-webhook-events', skipComponentAccess: true, requiredRole: 'system_admin' },
        { text: 'Async Event Queue', icon: <CloudSync />, path: '/admin/async-events', requiredRole: 'system_admin' },
      ]
    },
  ]
);

export const buildToolsMenu = (_counts: NavBadgeCounts): NavMenuItem[] => ([
    // Core item (ungrouped) — the portal landing / tool catalog
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/tools', skipComponentAccess: true, requiredRole: 'system_admin' },

    // Storage Group
    {
      group: 'tools_storage',
      label: 'Storage',
      icon: <Storage />,
      items: [
        { text: 'S3 Browser', icon: <Storage />, path: '/tools/s3-browser', skipComponentAccess: true, requiredRole: 'system_admin' },
      ]
    },

    // Database Group
    {
      group: 'tools_database',
      label: 'Database',
      icon: <Dns />,
      items: [
        { text: 'Query Console', icon: <Dns />, path: '/tools/db-console', skipComponentAccess: true, requiredRole: 'system_admin' },
        { text: 'Query Performance', icon: <Speed />, path: '/tools/db-performance', skipComponentAccess: true, requiredRole: 'system_admin' },
      ]
    },

    // AI Group
    {
      group: 'tools_ai',
      label: 'AI',
      icon: <Psychology />,
      items: [
        { text: 'AI Prompt Console', icon: <Psychology />, path: '/tools/ai-prompts', skipComponentAccess: true, requiredRole: 'system_admin' },
        { text: 'AI Interaction Log', icon: <History />, path: '/admin/ai-interactions', skipComponentAccess: true, requiredRole: 'system_admin' },
      ]
    },
  ]
);

